import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UserLayout from '../../components/user/UserLayout';
import { 
  ArrowLeft, 
  Eye, 
  UserPlus, 
  Radio, 
  QrCode, 
  Link as LinkIcon, 
  Target, 
  Share2, 
  MousePointerClick,
  TrendingUp,
  Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function UserDigitalCardAnalytics() {
  const navigate = useNavigate();
  const { cardId } = useParams();
  const [loading, setLoading] = useState(true);
  const [cardInfo, setCardInfo] = useState(null);
  const [analytics, setAnalytics] = useState({
    profile_opens: 245,
    contacts_saved: 87,
    nfc_taps: 156,
    qr_scans: 89,
    direct_links: 42,
    leads: 31,
    social_clicks: 164,
    link_clicks: 93
  });

  useEffect(() => {
    fetchCardInfo();
    // TODO: Fetch actual analytics from backend
    setTimeout(() => setLoading(false), 500);
  }, [cardId]);

  const fetchCardInfo = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${BACKEND_URL}/api/user/cards`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const card = data.cards?.find(c => c.transaction_id === cardId);
        setCardInfo(card);
      }
    } catch (err) {
      console.error('Failed to fetch card info:', err);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, bgColor, trend }) => (
    <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl ${bgColor}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        {trend && (
          <div className="flex items-center gap-1 text-sm text-green-600">
            <TrendingUp className="w-4 h-4" />
            <span className="font-semibold">+{trend}%</span>
          </div>
        )}
      </div>
      <div>
        <p className="text-3xl font-bold text-navy mb-1">{value.toLocaleString()}</p>
        <p className="text-sm text-gray-600 font-medium">{label}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <UserLayout>
        <div className="p-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading analytics...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/user/digital-cards')}
            className="flex items-center gap-2 text-gray-600 hover:text-navy mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Digital Cards
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-extrabold text-navy mb-2">Card Analytics</h1>
              <p className="text-gray-600">
                {cardInfo?.order_data?.displayName || 'Digital Card'} - #{cardId?.slice(0, 10)}
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>Last 30 days</span>
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-navy mb-4">Overview</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={Eye}
              label="Profile Opens"
              value={analytics.profile_opens}
              color="text-blue-600"
              bgColor="bg-blue-50"
              trend={12}
            />
            <StatCard
              icon={UserPlus}
              label="Contacts Saved"
              value={analytics.contacts_saved}
              color="text-green-600"
              bgColor="bg-green-50"
              trend={8}
            />
            <StatCard
              icon={Target}
              label="Leads Generated"
              value={analytics.leads}
              color="text-purple-600"
              bgColor="bg-purple-50"
              trend={15}
            />
            <StatCard
              icon={MousePointerClick}
              label="Total Interactions"
              value={analytics.profile_opens + analytics.social_clicks + analytics.link_clicks}
              color="text-orange-600"
              bgColor="bg-orange-50"
              trend={10}
            />
          </div>
        </div>

        {/* Engagement Metrics */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-navy mb-4">Engagement Metrics</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={Radio}
              label="NFC Taps"
              value={analytics.nfc_taps}
              color="text-indigo-600"
              bgColor="bg-indigo-50"
            />
            <StatCard
              icon={QrCode}
              label="QR Scans"
              value={analytics.qr_scans}
              color="text-cyan-600"
              bgColor="bg-cyan-50"
            />
            <StatCard
              icon={LinkIcon}
              label="Direct Links"
              value={analytics.direct_links}
              color="text-teal-600"
              bgColor="bg-teal-50"
            />
            <StatCard
              icon={Share2}
              label="Social Clicks"
              value={analytics.social_clicks}
              color="text-pink-600"
              bgColor="bg-pink-50"
            />
          </div>
        </div>

        {/* Link Performance */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-navy mb-4">Link Performance</h2>
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <StatCard
              icon={MousePointerClick}
              label="Custom Link Clicks"
              value={analytics.link_clicks}
              color="text-amber-600"
              bgColor="bg-amber-50"
              trend={18}
            />
          </div>
        </div>

        {/* Conversion Insights */}
        <div className="bg-gradient-to-br from-navy to-purple-600 rounded-2xl p-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Conversion Insights</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <p className="text-3xl font-bold mb-2">
                {((analytics.contacts_saved / analytics.profile_opens) * 100).toFixed(1)}%
              </p>
              <p className="text-white/80">Contact Save Rate</p>
            </div>
            <div>
              <p className="text-3xl font-bold mb-2">
                {((analytics.leads / analytics.profile_opens) * 100).toFixed(1)}%
              </p>
              <p className="text-white/80">Lead Conversion Rate</p>
            </div>
            <div>
              <p className="text-3xl font-bold mb-2">
                {((analytics.social_clicks / analytics.profile_opens) * 100).toFixed(1)}%
              </p>
              <p className="text-white/80">Social Engagement Rate</p>
            </div>
          </div>
        </div>

        {/* Note about data */}
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Analytics data shown is for demonstration purposes. Real-time analytics will be available once backend integration is complete.
          </p>
        </div>
      </div>
    </UserLayout>
  );
}
